/**
 * Replay-protection headers (security audit finding #14).
 *
 * Every state-changing request carries a fresh nonce + timestamp; the backend
 * remembers each nonce and refuses to process the same request twice. See
 * kvs_vsk_backend/docs/REPLAY_PROTECTION.md for the server side and the
 * rollout plan (the backend starts in monitor mode, so these headers are
 * accepted-but-optional until it is switched to enforce).
 *
 * The two header kinds behave in OPPOSITE ways — swapping them silently breaks
 * the guarantee:
 *
 *   X-Request-Id      NEW on every attempt, including a retry of the same
 *                     action. Reusing one is exactly what the server reads as
 *                     a replay (409 REPLAY_DETECTED).
 *   Idempotency-Key   IDENTICAL across retries of the same logical operation,
 *                     different for a genuinely new one — that is what lets the
 *                     server hand back the original response instead of running
 *                     the operation twice.
 */

/**
 * Unguessable random id. crypto.randomUUID needs a secure context (https or
 * localhost); KVs reaching a dev box over plain http on a LAN IP do not have
 * one, so fall back to crypto.getRandomValues, which works everywhere.
 */
export function randomRequestId(): string {
  const cryptoObj = window.crypto;
  if (typeof cryptoObj.randomUUID === 'function') {
    return cryptoObj.randomUUID();
  }
  const bytes = cryptoObj.getRandomValues(new Uint8Array(16));
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Fresh nonce + timestamp pair. Call per request — never cache the result. */
export function freshReplayHeaders(): Record<string, string> {
  return {
    'X-Request-Id': randomRequestId(),
    'X-Timestamp': String(Date.now()), // epoch MILLISECONDS — the server rejects seconds
  };
}
